import {
  DetailedHTMLProps,
  FC,
  InputHTMLAttributes,
  TextareaHTMLAttributes,
  useMemo,
  useState,
} from "react";
import classes from "./input.module.css";
import { cx } from "class-variance-authority";
import PasswordToggleIcon from "@/assets/icons/passwordToggleIcon";
import { Icons } from "@/assets";
import Loading from "@/components/Loading";

type Props = {
  label?: string;
  className?: string;
  labelClassName?: string;
  inputClassName?: string;
  isLoading?: boolean;
  disableLabelClick?: boolean;
  hideNativeInput?: boolean;
  variant?: "light" | "dark";
} & DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>;

const Input: FC<Props> = (props) => {
  const {
    variant = "light",
    label,
    className,
    labelClassName,
    inputClassName,
    isLoading,
    disableLabelClick,
    hideNativeInput: _hideNativeInput,
    ...inputProps
  } = props;

  const Wrapper = disableLabelClick ? "div" : "label";

  const [isPwVisible, setIsPwVisible] = useState(false);
  const _props = useMemo(
    () => ({
      ...inputProps,
      className: cx(
        classes.input,
        variant === "light"
          ? "border border-line-strong bg-white text-ink"
          : "border border-dark text-ink",
        inputClassName,
        {
          ["!pr-[40px]"]: inputProps.type === "password",
          ["bg-surface cursor-not-allowed text-muted"]: inputProps.disabled,
        },
      ),
    }),
    [inputProps, variant, inputClassName],
  );

  if (props.type === "checkbox") return <Checkbox {...props} />;

  if (props.type === "file") {
    return (
      <Wrapper className={cx(classes.inputGroup, className)}>
        <span className={cx("pl-5", labelClassName)}>{label}</span>

        <input
          {...inputProps}
          data-slot="input"
          className={cx(classes.input, classes.fileInput, inputClassName)}
        />
      </Wrapper>
    );
  }

  if (props.type === "radio") return <Radio {...props} />;

  const toggleVisibility = () => {
    setIsPwVisible((pw) => !pw);
  };

  return (
    <Wrapper className={cx(classes.inputGroup, className)}>
      {label && (
        <span className={cx("pl-5", labelClassName)}>{label}</span>
      )}

      {inputProps.type === "password" ? (
        <div className="password relative flex">
          <input {..._props} type={isPwVisible ? "text" : "password"} />
          <PasswordToggleIcon
            className={classes.toggleIcon}
            onClick={toggleVisibility}
          />
        </div>
      ) : (
        <input {..._props} />
      )}
      {isLoading && (
        <div className="absolute bottom-3.5 left-3">
          <Loading />
        </div>
      )}
    </Wrapper>
  );
};

export default Input;

const Checkbox: FC<Props> = ({
  className,
  inputClassName,
  hideNativeInput,
  ...props
}) => (
  <label className={cx(classes.checkbox, className)}>
    <input
      className={cx(inputClassName, {
        [classes.hiddenInput]: hideNativeInput,
      })}
      {...props}
    />
    <span className={classes.checkmark}>✔</span>
    {props.label}
  </label>
);

export const TextArea: FC<
  {
    label?: string;
    className?: string;
    labelClassName?: string;
    inputClassName?: string;
    variant?: "light" | "dark";
  } & DetailedHTMLProps<
    TextareaHTMLAttributes<HTMLTextAreaElement>,
    HTMLTextAreaElement
  >
> = ({
  variant = "light",
  label,
  className,
  labelClassName,
  inputClassName,
  ...textareaProps
}) => (
  <label className={cx(classes.inputGroup, className)}>
    {label && (
      <span className={cx("pl-5", labelClassName)}>{label}</span>
    )}
    <textarea
      {...textareaProps}
      className={cx(
        classes.input,
        "pt-5",
        variant === "light"
          ? "text-ink bg-white"
          : "border-dark text-ink border",
        inputClassName,
      )}
    />
  </label>
);

export const RadioSection: FC<Props> = (props) => (
  <div className="flex flex-col">
    <label className={cx(classes.inputGroup, props.className, "mb-3")}>
      {props.label}
    </label>
    <div className="flex flex-col gap-[10px]">{props.children}</div>
  </div>
);
const Radio: FC<Props> = ({
  className,
  inputClassName,
  hideNativeInput,
  ...props
}) => (
  <label
    className={cx(
      "group cursor-pointer transition-colors",
      props.checked
        ? "border-ink bg-ink text-paper"
        : "border-[#D8D4CD] bg-white text-ink hover:bg-[#F3F1EC]",
      classes.radio,
      className,
    )}
  >
    <span className="flex-1">{props.label}</span>
    <input
      type="radio"
      className={cx(inputClassName, {
        [classes.hiddenInput]: hideNativeInput,
      })}
      {...props}
    />
    <div className="relative flex h-5 w-5 items-center justify-center overflow-hidden rounded-full">
      <span
        className={cx(
          "absolute h-5 w-5 rounded-full border-2 border-solid transition-colors",
          props.checked
            ? "border-white"
            : "border-[#BDB7AE] group-hover:border-ink",
        )}
      />
      <span className={`${props.checked ? "" : "hidden"} absolute scale-75`}>
        <Icons.Checkmark />
      </span>
    </div>
  </label>
);
